import { ThemeHelper } from "./theme-helper/ThemeHelper";
import { WishlistEvents } from "./utils/WishlistEvents";
import { WishlistActionName, WishlistState } from "./state/WishlistState";
import { UrlUtil } from "./utils/UrlUtil";
import { MoneyUtil } from "./utils/MoneyUtil";
import { ProductUtil } from "./utils/ProductUtil";
import { StorefrontApi } from "./api/StorefrontApi";
import { WishlistApi } from "./api/WishlistApi";
import { ShopifyApi } from "./api/ShopifyApi";
import { TrackingApi } from "./api/TrackingApi";
import { createDebug } from "./utils/LazyDebug";
import type { DialogElement } from "./WishlistElement";
import type { AppLoader, AssetSettings } from "./AppLoader";

const debug = createDebug("wk:app");

export class WishlistApp {
  routes: Record<string, string>;
  settings: WishlistKingParams["settings"];
  config: ReturnType<typeof this.parseConfig>;
  customer: { id: string; email: string; name: string; tags: string[] } | null;
  events: WishlistEvents;
  theme: ThemeHelper;

  private _state: WishlistState;
  private _utils: WishlistUtils;
  private _apis: {
    wishlist: WishlistApi;
    shopify: ShopifyApi;
    storefront: StorefrontApi;
    tracking: TrackingApi;
  };

  public get state() {
    return this._state;
  }

  public get apis() {
    return this._apis;
  }

  public get utils() {
    return this._utils;
  }

  public get hasSession() {
    return !!this.getSessionId() || !!this.customer;
  }

  public get isLoggedIn() {
    return !!this.customer?.id;
  }

  getSessionId() {
    const sessionId = window.localStorage.getItem("wk_session_id");
    if (sessionId) {
      return sessionId;
    }

    const legacySession = window.localStorage.getItem("appmate-session");
    if (legacySession) {
      try {
        const sessionId = JSON.parse(legacySession).state.sid as string;
        return this.setSessionId(sessionId);
      } catch (error) {
        console.error(error);
      }
    }
  }

  setSessionId(sessionId: string) {
    window.localStorage.setItem("wk_session_id", sessionId);
    this._apis?.tracking.setSessionId(sessionId);
    return sessionId;
  }

  clearSessionId() {
    window.localStorage.removeItem("wk_session_id");
    this._apis?.tracking.clearSessionId();
    this._apis?.wishlist.clearSessionId();
  }

  addToCart: typeof this.state.addToCart = (args) => this.state.addToCart(args);
  addAllToCart: typeof this.state.addAllToCart = (args) =>
    this.state.addAllToCart(args);
  addWishlistItem: typeof this.state.addWishlistItem = (args) =>
    this.state.addWishlistItem(args);
  saveForLater: typeof this.state.saveForLater = (args) =>
    this.state.saveForLater(args);
  clearWishlist: typeof this.state.clearWishlist = () =>
    this.state.clearWishlist();
  removeWishlistItem: typeof this.state.removeWishlistItem = (args) =>
    this.state.removeWishlistItem(args);
  shareWishlist: typeof this.state.shareWishlist = (args) =>
    this.state.shareWishlist(args);
  updateWishlistItem: typeof this.state.updateWishlistItem = (args) =>
    this.state.updateWishlistItem(args);

  currentDialog?: DialogElement | null;

  async showDialog(name: DialogName, props?: AssetSettings) {
    if (this.currentDialog) {
      this.currentDialog.close();
      this.currentDialog = null;
    }

    const dialogUrl = this.config.dialogs[name];
    if (!dialogUrl) {
      console.warn(`Dialog ${name} not found`);
      return;
    }

    const { default: loader, appSettings } = (await import(
      window.WishlistKingAppLoaderURL
    )) as { default: AppLoader; appSettings: any };

    const customElementName = `wishlist-${name}-dialog`;
    await loader.load({
      type: "lit-component",
      name: customElementName,
      url: dialogUrl,
      dependencies: [
        {
          url: appSettings.assets.componentAccountDialogCss,
          order: 5,
        },
      ],
    });

    const Dialog = customElements.get(customElementName);
    if (!Dialog) {
      console.warn(`Dialog ${name} not defined`);
      return;
    }

    this.currentDialog = new Dialog() as DialogElement;

    if (props) {
      Object.assign(this.currentDialog, props);
    }

    this.currentDialog?.show();
  }

  constructor(params: WishlistKingParams) {
    this.config = this.parseConfig(params);
    this.settings = this.parseSettings(params);
    this.routes = this.parseRoutes();
    this.customer = this.config.customer.id
      ? {
          id: this.config.customer.id!,
          email: this.config.customer.email!,
          name: this.config.customer.name!,
          tags: this.config.customer.tags!,
        }
      : null;

    debug("Create app", {
      config: this.config,
      settings: this.settings,
      customer: this.customer,
    });

    this.events = new WishlistEvents();
    this.theme = new ThemeHelper({
      rootUrl: this.routes.rootUrl,
      wishlistUrl: this.routes.wishlistUrl,
    });

    this._utils = {
      url: new UrlUtil({
        rootUrl: this.routes.rootUrl,
        wishlistUrl: this.routes.wishlistUrl,
        shopDomain: this.config.shop.domain,
        placeholderImageUrl: this.config.products.fallbackImage,
        fileUrl: this.routes.fileUrl,
      }),
      money: new MoneyUtil({
        moneyFormat: this.config.shop.moneyFormat,
        moneyFormatWithCurrency: this.config.shop.moneyFormatWithCurrency,
      }),
      product: new ProductUtil(),
    };

    const storefrontApi = new StorefrontApi({
      accessToken: this.config.storefront.accessToken,
      version: this.config.storefront.version,
      country: window.Shopify.country.toUpperCase(),
      language: this.config.storefront.language,
      // language: window.Shopify.locale.split("-")[0].toUpperCase(),
      storeName: this.config.shop.permanentDomain.replace(".myshopify.com", ""),
    });

    this._apis = {
      wishlist: new WishlistApi({
        host: this.config.routes.apiHost,
        shop: this.config.shop.permanentDomain,
        customerId: this.config.customer.id ?? undefined,
        sessionId: this.getSessionId(),
        trackingSource: this.theme.getPageType(),
        storefrontApi,
      }),
      shopify: new ShopifyApi({
        host: window.Shopify.routes.root.substring(
          0,
          window.Shopify.routes.root.length - 1,
        ),
      }),
      storefront: storefrontApi,
      tracking: new TrackingApi({
        notifyHost: this.settings.general.wishlistPath.startsWith("/apps")
          ? this.settings.general.wishlistPath
          : this.config.routes.apiHost,
        events: this.events,
        shop: this.config.shop.permanentDomain,
        customerId: this.config.customer.id ?? undefined,
        customerEmail: this.config.customer.email ?? undefined,
        sessionId: this.getSessionId(),
        utils: this.utils,
        theme: this.theme,
        settings: this.settings,
      }),
    };

    this._state = new WishlistState({
      wishlistApi: this.apis.wishlist,
      shopifyApi: this.apis.shopify,
      storefrontApi: this.apis.storefront,
      settings: this.settings,
      events: this.events,
      utils: this.utils,
    });

    // this.initLoginActionQueue();
    this.initCustomerSession();
    this.initUrlChangeEvents();
    this.trackCurrentPage();
    this.initLoginRedirect();

    window.WishlistKing = this;

    debug("App ready");

    this.events.publish({
      name: "app:ready",
      data: {},
    });
  }

  public initLoginRedirect() {
    const currentPage = this.theme.getPageType();
    if (currentPage === "account") {
      const pending = this.getPendingWishlistAction();
      if (pending) {
        debug("Inject redirect for pending action", { pending });
        const loginForm = document.getElementById("customer_login");
        const signupForm = document.getElementById("create_customer");
        (loginForm ?? signupForm)?.insertAdjacentHTML(
          "beforeend",
          `<input type="hidden" name="return_to" value="${
            pending.origin ?? this.routes.wishlistUrl
          }">`,
        );
      }
    }
  }

  public trackCurrentPage() {
    const currentPage = this.theme.getPageType();
    if (currentPage === "wishlist") {
      const wishlistId = this.theme.getWishlistId(window.location.pathname);
      if (wishlistId) {
        this.state
          .publishEvent({
            eventName: "wishlist:page-view",
            wishlistId,
          })
          .catch((error) => {
            console.warn("Failed to track wishlist page view", error);
          });
      }
    }
  }

  public async initLoginActionQueue() {
    debug("Init customer events");

    const loggedInState = !!window.localStorage.getItem("wk_logged_in");

    this.events.subscribe("wishlist:login-required", (event) => {
      const queueableActions: WishlistActionName[] = [
        "wishlist:add",
        "wishlist:view",
        "wishlist:save-for-later",
      ];
      if (event.data?.action && queueableActions.includes(event.data.action)) {
        window.localStorage.setItem(
          "wk_pending",
          JSON.stringify({
            ...event.data,
            origin: window.location.pathname + window.location.search,
          }),
        );
      }
    });

    if (!loggedInState && this.isLoggedIn) {
      debug("Customer logged in");
      window.localStorage.setItem("wk_logged_in", "1");
      const pending = this.getPendingWishlistAction();
      this.events.publish({
        name: "customer:login",
        data: {
          pending,
        },
      });
    } else if (loggedInState && !this.isLoggedIn) {
      debug("Customer logged out");
      window.localStorage.removeItem("wk_logged_in");
      this.events.publish({
        name: "customer:logout",
        data: {},
      });
    }

    if (!["challenge", "account"].includes(this.theme.getPageType())) {
      window.localStorage.removeItem("wk_pending");
    }
  }

  private initCustomerSession() {
    if (!this.hasSession) {
      // await this.clearCustomerSession();
      this.events.once("app:init-sid", (event) => {
        if (event.data?.sessionId) {
          this.setSessionId(event.data?.sessionId);
        }
      });
    }
  }

  public async clearCustomerSession() {
    window.localStorage.removeItem("wk_pending");
    this.clearSessionId();
    this.initCustomerSession();
    await this.state.revalidate(true);
  }

  getPendingWishlistAction() {
    try {
      const pendingActionJson = window.localStorage.getItem("wk_pending");
      if (pendingActionJson) {
        return JSON.parse(pendingActionJson) as {
          action: WishlistActionName;
          productHandle?: string;
          varianId?: number;
          origin?: string;
        };
      }
    } catch (error) {
      console.warn("Could not parse pending wishlist action", error);
    }
  }

  private initUrlChangeEvents() {
    const publishVariantChange = ({
      productHandle,
      variantId,
    }: {
      productHandle: string;
      variantId?: number;
    }) => {
      this.events.publish({
        name: "product:change-variant:success",
        data: {
          productHandle,
          variantId,
        },
      });
    };

    const createHistoryChangeHandler =
      (originalFunction: typeof history.replaceState) =>
      (...args: Parameters<typeof history.replaceState>) => {
        const result = originalFunction.apply(history, args);

        let path = typeof args[2] === "string" ? args[2] : args[2]?.toString();

        if (!path) return result;
        if (path.startsWith("?")) {
          path = window.location.pathname + path;
        }

        const baseUrl = `${window.location.protocol}//${window.location.host}`;
        const url = new URL(path, baseUrl);

        const productHandle = this.theme.getProductHandle(url);
        const variantId = url.searchParams.get("variant");

        if (!productHandle) {
          return result;
        }

        publishVariantChange({
          productHandle,
          variantId: variantId ? parseInt(variantId) : undefined,
        });
      };

    history.replaceState = createHistoryChangeHandler(history.replaceState);
    history.pushState = createHistoryChangeHandler(history.pushState);
  }

  private parseConfig({
    config,
  }: Omit<WishlistKingParams, "settings" | "stylesheets">) {
    let storefrontLang =
      config.localeIsoCode?.toUpperCase().replace(/-/g, "_") ?? "EN";

    if (!storefrontLang.startsWith("ZH")) {
      storefrontLang = storefrontLang.split("_")[0];
    }

    return {
      storefront: {
        accessToken: config.storefrontAccessToken,
        version: config.storefrontVersion,
        language: storefrontLang,
      },
      customer: {
        id: config.customerId ? config.customerId : null,
        email: config.customerEmail ?? "",
        name: config.customerName ?? "",
        tags: config.customerTags ?? [],
      },
      shop: {
        id: config.shopId,
        name: config.shopName ?? "",
        domain: config.shopDomain ?? "",
        permanentDomain: config.shopPermanentDomain ?? "",
        moneyFormat: config.shopMoneyFormat ?? "",
        moneyFormatWithCurrency: config.shopMoneyFormatWithCurrency ?? "",
        customerAccountsEnabled: config.shopCustomerAccountsEnabled,
      },
      routes: {
        apiHost: config.apiHost,
        sdkHost: config.sdkHost,
        templateHost: config.templateHost,
        rootUrl: window.Shopify.routes.root,
        fileUrl: config.fileUrl,
      },
      products: {
        fallbackImage:
          config.productFallbackImage ??
          "//cdn.shopify.com/s/files/1/0533/2089/files/placeholder-images-image_large.png?format=webp&v=1530129081",
      },
      dialogs: {
        account: config.accountDialogUrl,
      },
    };
  }

  private parseSettings({
    settings,
  }: {
    settings: Partial<WishlistSettings>;
  }): WishlistSettings {
    return {
      general: {
        appLoading: settings?.general?.appLoading ?? "LAZY",
        wishlistAccessMode:
          settings?.general?.wishlistAccessMode ?? "UNRESTRICTED",
        wishlistMode: settings?.general?.wishlistMode ?? "PRODUCT",
        wishlistPath: settings?.general?.wishlistPath ?? "/apps/wishlist",
      },
      money: {
        withCurrency: settings?.money?.withCurrency ?? false,
      },
      integrations: {
        googleAnalytics: {
          enabled: settings.integrations?.googleAnalytics?.enabled ?? false,
          tracker: settings.integrations?.googleAnalytics?.tracker ?? "",
        },
        klaviyo: {
          enabled: settings.integrations?.klaviyo?.enabled ?? false,
        },
        metaPixel: {
          enabled: settings.integrations?.metaPixel?.enabled ?? false,
        },
        wishlistAnalytics: {
          enabled: settings.integrations?.wishlistAnalytics?.enabled ?? true,
        },
      },
      icons: {},
    };
  }

  private parseRoutes() {
    const rootUrl = this.config.routes.rootUrl;

    const wishlistUrl = this.settings.general.wishlistPath.startsWith(
      "https://",
    )
      ? this.settings.general.wishlistPath
      : rootUrl +
        this.settings.general.wishlistPath
          .split("/")
          .filter((v) => !!v)
          .join("/");

    return {
      rootUrl: rootUrl,
      fileUrl: this.config.routes.fileUrl,
      wishlistUrl,
      accountUrl: rootUrl + "account",
      accountLoginUrl: rootUrl + "account/login",
      accountLogoutUrl: rootUrl + "account/logout",
      accountRegisterUrl: rootUrl + "account/register",
      accountAddressesUrl: rootUrl + "account/addresses",
      collectionsUrl: rootUrl + "collections",
      allProductsCollectionUrl: rootUrl + "collections/all",
      searchUrl: rootUrl + "search",
      predictiveSearchUrl: rootUrl + "search/suggest",
      cartUrl: rootUrl + "cart",
      cartAddUrl: rootUrl + "cart/add",
      cartChangeUrl: rootUrl + "cart/change",
      cartClearUrl: rootUrl + "cart/clear",
      cartUpdateUrl: rootUrl + "cart/update",
      productRecommendationsUrl: rootUrl + "recommendations/products",
    };
  }

  async querySaveForLaterButton({
    productHandle,
    variantId,
  }: {
    productHandle: string;
    variantId: string | number;
  }) {
    const saveForLaterQuery = `save-for-later[data-product-handle="${productHandle}"][data-variant-id="${variantId}"] .wk-button`;
    const saveForLaterButton = document.querySelector(saveForLaterQuery);

    if (!saveForLaterButton) {
      let resolved = false;
      return new Promise((resolve) => {
        this.theme.watch(
          {
            selector: saveForLaterQuery,
          },
          (target) => {
            if (!resolved) {
              resolve(target.element);
              resolved = true;
            }
          },
        );
      });
    }

    return saveForLaterButton;
  }
}

export interface WishlistKingParams {
  config: {
    customerId: string;
    customerEmail: string;
    customerName: string;
    customerTags: string[];
    shopId: number;
    shopName: string;
    shopDomain: string;
    shopPermanentDomain: string;
    shopMoneyFormat: string;
    shopMoneyFormatWithCurrency: string;
    shopCustomerAccountsEnabled: string;
    fileUrl: string;
    productFallbackImage?: string;
    apiHost: string;
    sdkHost: string;
    templateHost: string;
    storefrontVersion: string;
    storefrontAccessToken: string;
    accountDialogUrl?: string;
    localeIsoCode?: string;
  };
  settings: WishlistSettings;
}

export interface WishlistSettings {
  general: {
    wishlistAccessMode:
      | "UNRESTRICTED"
      | "REQUIRE_LOGIN_BEFORE_USE"
      | "REQUIRE_LOGIN_TO_VIEW_LIST";
    wishlistMode: "PRODUCT" | "VARIANT";
    wishlistPath: string;
    appLoading: "LAZY" | "EAGER";
  };
  money: {
    withCurrency: boolean;
  };
  integrations: {
    wishlistAnalytics: { enabled: boolean };
    klaviyo: { enabled: boolean };
    metaPixel: { enabled: boolean };
    googleAnalytics: { enabled: boolean; tracker: string };
  };
  icons: Record<string, string>;
}

export interface WishlistUtils {
  money: MoneyUtil;
  url: UrlUtil;
  product: ProductUtil;
}

export interface CustomerPrivacyApi {
  marketingAllowed: () => boolean;
  saleOfDataAllowed: () => boolean;
  analyticsProcessingAllowed: () => boolean;
  preferencesProcessingAllowed: () => boolean;
}

export type LoadFeaturesCallback = (error: false | Error) => void;

export interface ShopifyFeature {
  name: "consent-tracking-api";
  version: "0.1";
}

export type DialogName = "account";

declare global {
  interface Window {
    WishlistKing?: WishlistApp;
    WishlistKingAppLoaderURL: string;
    Shopify: {
      locale: string;
      country: string;
      shop: string;
      routes: {
        root: string;
      };
      currency: {
        rate: string;
        active: string;
      };
      loadFeatures: (
        features: ShopifyFeature[],
        callback: LoadFeaturesCallback,
      ) => void;
      customerPrivacy?: CustomerPrivacyApi;
    };
    fbq?: (cmd: string, event: string, data: object) => void;
    gtag?: (cmd: string, name: string, ...args: (string | object)[]) => void;
    klaviyo?: Array<["track" | "identify", any, any?]>;
    dataLayer?: Record<string, unknown>[];
  }
}
